import { getAuthToken, request } from "@/shared/http/client";
import { followStream, type StreamHandlers, type StreamOutcome } from "./stream";
import type { Say } from "./tool-labels";
import { bodyOf as importBody } from "./turn-body";

/**
 * VOLTAR PARA UM TURNO QUE AINDA CORRE.
 *
 * ── O QUE MUDOU DO OUTRO LADO ─────────────────────────────────────────────
 *
 * O servidor deixou de matar o turno quando o fio cai (`live-turns.ts`). Trocar
 * de app no telemóvel, sair da rota ou recarregar a página desliga um espectador,
 * não o turno. Este módulo é como se volta a ser espectador: o servidor repete
 * tudo o que já foi dito e segue dali, para voltar ser indistinguível de nunca
 * ter saído.
 *
 * ── POR QUE `fetch` E NÃO `EventSource` ───────────────────────────────────
 *
 * A mesma razão de sempre, e é decisiva: `EventSource` não leva um cabeçalho
 * `Authorization`. Ler o corpo à mão dá também um cancelamento que de facto pára
 * o pedido — e cancelar AQUI já não pára o turno, que é o ponto de tudo isto.
 */
const API_BASE = import.meta.env.VITE_API_BASE ?? "";

/**
 * Liga-se ao turno em voo deste agente.
 *
 * `"none"` — e não um erro — quando não havia nenhum: é a resposta ordinária, e
 * de longe a mais comum. Quem a recebe volta a ler a sessão, que é onde uma
 * resposta já terminada está guardada.
 */
export function attachToLiveTurn(
	agent: string,
	handlers: StreamHandlers,
	signal: AbortSignal,
	say: Say,
	sessionId?: string | null,
): Promise<StreamOutcome> {
	const query = new URLSearchParams({ agent });
	if (sessionId) query.set("sessionId", sessionId);
	// Um erro aqui não vale um alarme no ecrã: ninguém PEDIU esta ligação — ela é
	// uma tentativa de recuperar. Falhar em silêncio e reler a sessão é honesto.
	return attach(`/v3/agents/run/live?${query}`, handlers, signal, say, "dropped");
}

/**
 * A ESCRITA DE UMA MISSÃO, ao vivo — o Ben e cada escritor paralelo (`lane`).
 *
 * Mesmo fio, mesmo leitor. Uma recusa (404 de um servidor sem a porta, 403)
 * lê-se como `none`: quem chamou volta à leitura de sempre e não insiste — um
 * `dropped` aqui seria religar a cada poucos segundos contra uma porta fechada.
 */
export function attachToMission(
	id: string,
	handlers: StreamHandlers,
	signal: AbortSignal,
	/** O pensamento como foi escrito, em tempo real — e o servidor não traduz para este fio. */
	original = false,
): Promise<StreamOutcome> {
	return attach(
		`/v3/agents/missions/${encodeURIComponent(id)}/live${original ? "?thoughts=original" : ""}`,
		handlers,
		signal,
		(key) => key,
		"none",
	);
}

async function attach(
	path: string,
	handlers: StreamHandlers,
	signal: AbortSignal,
	say: Say,
	refused: StreamOutcome,
): Promise<StreamOutcome> {
	const token = await getAuthToken();
	const headersController = new AbortController();
	const headersTimer = setTimeout(() => headersController.abort(), 15_000);
	let response: Response;
	try {
		response = await fetch(`${API_BASE}${path}`, {
			headers: token ? { authorization: `Bearer ${token}` } : {},
			signal: AbortSignal.any([signal, headersController.signal]),
		});
	} catch {
		// A rede que derrubou o fio provavelmente ainda está a recuperar. Nada foi
		// perdido: o turno continua lá, e quem chamou tenta o outro caminho.
		return signal.aborted ? "aborted" : "dropped";
	} finally {
		clearTimeout(headersTimer);
	}
	if (!response.ok || !response.body) return refused;
	return followStream(response.body, handlers, say, signal);
}

/**
 * PARAR — agora o único cancelamento que existe.
 *
 * Deixou de ser um efeito colateral de fechar uma aba: abortar o `fetch` já só
 * fecha a janela. Quem quer que o turno pare de custar dinheiro tem de o dizer,
 * e isto é dizê-lo.
 */
export async function stopTurn(agent: string, sessionId?: string | null): Promise<void> {
	const token = await getAuthToken();
	const query = new URLSearchParams({ agent });
	if (sessionId) query.set("sessionId", sessionId);
	await fetch(`${API_BASE}/v3/agents/run/stop?${query}`, {
		method: "POST",
		headers: token ? { authorization: `Bearer ${token}` } : {},
		// Um "parar" que falha na rede não tem nada a dizer ao ecrã: o botão já
		// parou de mostrar o turno, e o servidor terminá-lo-á sozinho.
	}).catch(() => undefined);
}

export function queueTurnMessage(
	id: string,
	message: import("./stream").StreamInput,
): Promise<{ accepted: boolean }> {
	return request("/v3/agents/run/queue", {
		method: "POST",
		body: { id, message: importBody(message) },
	});
}
